
import React, { Suspense, lazy, useState, useEffect, useCallback, useRef } from 'react';
import { NavigationDock } from './components/NavigationDock';
import { Header } from './components/Header';
import { Chatbot } from './components/Chatbot';
import { UserActionFeedbackCenter } from './components/UserActionFeedbackCenter';
import { HistoricalPlayer } from './components/HistoricalPlayer';
import { EnergyPlusTestbedBar } from './components/EnergyPlusTestbedBar';
import { GuidedDemo } from './components/GuidedDemo';
import { LoginPage } from './components/LoginPage';
import { APSViewer } from './components/APSViewer';
import { ModelWorkspaceSelector, type ModelWorkspaceTarget } from './components/ModelWorkspaceSelector';
import { SensorData, ControlMode } from './types';
import { BMSSimulationService } from './services/BMSSimulationService';
import {
  HISTORICAL_TEST_WINDOW_END_SECONDS,
  HVAC_OPERATION_START_SECONDS,
  HistoricalService,
} from './services/HistoricalService';
import { BuildingConfigService } from './services/BuildingConfigService';
import { DataBridge, type DataSourceMode } from './services/DataBridge';
import { getPreferredDataSourceMode, setPreferredDataSourceMode } from './services/DataSourcePreference';
import { TwinContextService, TwinFrame } from './services/TwinContextService';
import { EnergyPlusTestbedService, type EnergyPlusOperatingMode } from './services/EnergyPlusTestbedService';
import { AuthService, AuthUser } from './services/AuthService';
import {
  markTrackingRunAccepted,
  markTrackingRunFailed,
  markTrackingRunRequested,
  trackingRunKeyFromStatus,
} from './services/TrackingRunSession';

const LAZY_RELOAD_SESSION_KEY = 'aees:lazy-chunk-reload:v1';

const isLazyChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk .* failed/i.test(message);
};

const lazyWithReload = <T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
) => lazy(async () => {
  try {
    const module = await loader();
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(LAZY_RELOAD_SESSION_KEY);
    return module;
  } catch (error) {
    if (typeof window !== 'undefined' && isLazyChunkLoadError(error)) {
      const lastReload = Number(window.sessionStorage.getItem(LAZY_RELOAD_SESSION_KEY) || 0);
      if (!Number.isFinite(lastReload) || Date.now() - lastReload > 15_000) {
        window.sessionStorage.setItem(LAZY_RELOAD_SESSION_KEY, String(Date.now()));
        window.location.reload();
        return await new Promise<{ default: T }>(() => {});
      }
    }
    throw error;
  }
});

class WorkspaceErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Workspace] 화면 모듈을 불러오지 못했습니다.', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#e8eef1] p-6">
        <div className="max-w-md rounded-3xl border border-[#bd4655]/20 bg-white p-6 text-center shadow-[0_18px_45px_rgba(15,35,45,0.12)]">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f8e9eb] text-[#bd4655]">
            <i className="fa-solid fa-rotate" />
          </div>
          <h2 className="mt-4 text-base font-bold text-[#142b38]">최신 화면을 다시 불러와야 합니다</h2>
          <p className="mt-2 text-xs leading-relaxed text-[#4e6572]">
            서버 빌드가 갱신되어 이전 화면 파일을 사용할 수 없습니다. 새로고침하면 로그인 상태를 유지한 채 복구합니다.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 h-10 rounded-xl bg-[#0f8b8d] px-5 text-xs font-bold text-white transition hover:bg-[#08777d]"
          >
            최신 화면 새로고침
          </button>
        </div>
      </div>
    );
  }
}

const SystemLoop = lazyWithReload(() => import('./views/SystemLoop').then(m => ({ default: m.SystemLoop })));
const FloorInfo = lazyWithReload(() => import('./views/FloorInfo').then(m => ({ default: m.FloorInfo })));
const AIControl = lazyWithReload(() => import('./views/AIControl').then(m => ({ default: m.AIControl })));
const AIExplainability = lazyWithReload(() => import('./views/AIExplainability').then(m => ({ default: m.AIExplainability })));
const ModelManagement = lazyWithReload(() => import('./views/ModelManagement').then(m => ({ default: m.ModelManagement })));
const DigitalTwin = lazyWithReload(() => import('./views/DigitalTwin').then(m => ({ default: m.DigitalTwin })));
const Building3D = lazyWithReload(() => import('./views/Building3D').then(m => ({ default: m.Building3D })));
const Reporting = lazyWithReload(() => import('./views/Reporting').then(m => ({ default: m.Reporting })));
const Settings = lazyWithReload(() => import('./views/Settings').then(m => ({ default: m.Settings })));

const ACTIVE_MODEL_STORAGE_KEY = 'aees-active-model-urn';
const HOME_VIEWER_MODE_STORAGE_KEY = 'aees-home-viewer-mode';
const ACTIVE_TAB_STORAGE_KEY = 'aees:dashboard-active-tab:v1';
const CONTROL_MODE_STORAGE_KEY = 'aees:dashboard-control-mode:v1';
const PERSISTED_TABS = new Set([
  'overview', 'digital-twin', 'loop-water', 'loop-air', 'ai-control',
  'ai-explain', 'ai-model', 'reporting', 'settings',
]);
const PREFERRED_REVIT_MODEL_NAME = '0620SEDC (1).rvt';
const APS_VIEWER_SCRIPT_URL = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
const APS_VIEWER_STYLE_URL = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';

type HomeViewerMode = 'operational' | 'revit-prep' | 'revit';

const ViewLoading = () => (
  <div className="h-full w-full flex items-center justify-center bg-slate-950">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-300 rounded-full animate-spin" />
      <p className="text-[10px] font-orbitron text-slate-500 uppercase tracking-widest">Loading workspace...</p>
    </div>
  </div>
);

const Home3DLoading = () => (
  <div className="h-full w-full flex items-center justify-center bg-slate-950/80">
    <div className="flex flex-col items-center gap-3">
      <div className="h-12 w-12 rounded-xl border border-cyan-500/20 bg-cyan-500/5 flex items-center justify-center text-cyan-300">
        <i className="fa-solid fa-cube text-lg" />
      </div>
      <p className="text-[10px] font-orbitron text-slate-400 uppercase tracking-widest">Preparing 3D twin</p>
      <p className="text-[9px] font-mono text-slate-600">Operational controls are ready while the renderer loads.</p>
    </div>
  </div>
);

const RevitViewerPreparing: React.FC<{ onCancel: () => void }> = ({ onCancel }) => (
  <div className="flex h-full w-full items-center justify-center bg-[#e8eef1]">
    <div className="max-w-sm rounded-3xl border border-[#143746]/15 bg-white/95 px-6 py-5 text-center shadow-[0_18px_45px_rgba(15,35,45,0.13)]">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e7f5f5] text-[#08777d]">
        <i className="fa-solid fa-cube text-lg" />
      </div>
      <p className="mt-4 font-orbitron text-[10px] font-bold uppercase tracking-[0.22em] text-[#08777d]">
        REVIT VIEWER PREP
      </p>
      <h2 className="mt-2 text-base font-bold text-[#142b38]">APS Viewer 시작 준비 중</h2>
      <p className="mt-2 text-xs leading-relaxed text-[#4e6572]">
        운영 3D의 WebGL 자원을 정리한 뒤 Revit 검토 뷰어를 시작합니다.
      </p>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-[#e2eaed]">
        <div className="h-full w-2/3 animate-pulse rounded-full bg-[#0f8b8d]" />
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="mt-4 rounded-xl border border-[#c7d5d6] bg-white px-4 py-2 text-xs font-bold text-[#4e6572] transition hover:bg-[#edf4f6] hover:text-[#08777d]"
      >
        운영 3D로 돌아가기
      </button>
    </div>
  </div>
);

// 건물 설정 로드 후 시뮬레이션 시작
const readStorage = (key: string) => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // localStorage can be unavailable in restricted browser contexts.
  }
};

const readPersistedTab = () => {
  if (typeof window !== 'undefined') {
    const testbedMode = new URLSearchParams(window.location.search).get('mode');
    if (testbedMode === 'energyplus-validation') return 'ai-control';
    if (testbedMode === 'closed-loop-testbed') return 'overview';
  }
  const value = readStorage(ACTIVE_TAB_STORAGE_KEY);
  return PERSISTED_TABS.has(value) || value.startsWith('floor-') ? value : 'overview';
};

const readPersistedControlMode = () => {
  const value = readStorage(CONTROL_MODE_STORAGE_KEY);
  return Object.values(ControlMode).includes(value as ControlMode)
    ? value as ControlMode
    : ControlMode.DECISION_SUPPORT;
};

const RevitModelReview: React.FC<{
  modelUrn: string;
  onViewerModeChange: (mode: HomeViewerMode) => void;
  onOpenSettings: () => void;
}> = ({ modelUrn, onViewerModeChange, onOpenSettings }) => {
  const [apsReady, setApsReady] = useState(() => !!(window as any).Autodesk?.Viewing);
  const [apsError, setApsError] = useState<string | null>(null);
  const [activeLevelName, setActiveLevelName] = useState<string | null>(null);

  useEffect(() => {
    if ((window as any).Autodesk?.Viewing) {
      setApsReady(true);
      return;
    }
    setApsError(null);

    if (!document.querySelector(`link[href^="${APS_VIEWER_STYLE_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = APS_VIEWER_STYLE_URL;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = `${APS_VIEWER_SCRIPT_URL}?aees_retry=${Date.now()}`;
    script.async = true;
    script.onload = () => {
      if ((window as any).Autodesk?.Viewing) {
        setApsReady(true);
      } else {
        setApsError('APS Viewer SDK는 로드됐지만 Autodesk 전역 객체를 찾지 못했습니다.');
      }
    };
    script.onerror = () => setApsError('APS Viewer SDK 로드에 실패했습니다.');
    document.head.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#e8eef1]">
      {apsReady ? (
        <APSViewer
          modelUrn={modelUrn}
          showLevelPicker
          disableCustomHome
          defaultViewType="3d"
          onLevelChange={setActiveLevelName}
        />
      ) : apsError ? (
        <div className="flex h-full w-full items-center justify-center bg-[#e8eef1]">
          <div className="max-w-md rounded-2xl border border-[#bd4655]/25 bg-white px-6 py-5 text-center shadow-[0_18px_45px_rgba(15,35,45,0.12)]">
            <p className="font-orbitron text-[10px] font-bold uppercase tracking-[0.22em] text-[#bd4655]">APS VIEWER ERROR</p>
            <p className="mt-2 text-sm font-bold text-[#142b38]">{apsError}</p>
            <p className="mt-2 text-xs leading-relaxed text-[#4e6572]">네트워크 또는 Autodesk Viewer CDN 응답을 확인한 뒤 다시 새로고침하세요.</p>
          </div>
        </div>
      ) : (
        <Home3DLoading />
      )}

      <div className="pointer-events-none absolute left-4 top-4 z-50 max-w-[390px] rounded-2xl border border-[#143746]/15 bg-white/[0.92] px-5 py-4 shadow-[0_18px_45px_rgba(15,35,45,0.12)] backdrop-blur md:left-32">
        <p className="font-orbitron text-[10px] font-bold uppercase tracking-[0.28em] text-[#08777d]">
          REVIT MODEL REVIEW
        </p>
        <h2 className="mt-1 text-base font-bold text-[#142b38]">0620SEDC 3F 수정 Revit 모델</h2>
        <p className="mt-1 text-xs leading-relaxed text-[#4e6572]">
          최신 3F Room/Boundary 수정 Revit 변환본을 대시보드에서 직접 확인하는 검토 화면입니다. 층 버튼과 POV 기능으로 내부 공간감을 확인하세요.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold text-[#08777d]">
          <span className="rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-3 py-1">SVF2 변환 완료</span>
          <span className="rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-3 py-1">
            {activeLevelName ? `${activeLevelName} 선택` : '전체 모델'}
          </span>
          <span className="rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-3 py-1">최신 수정 모델</span>
        </div>
      </div>

    </div>
  );
};

const RevitModelReviewV2: React.FC<{
  modelUrn: string;
  onViewerModeChange: (mode: HomeViewerMode) => void;
  onOpenSettings: () => void;
}> = ({ modelUrn, onViewerModeChange, onOpenSettings }) => {
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const levelsExtRef = useRef<any>(null);
  const bimWalkExtRef = useRef<any>(null);
  const povTimerRef = useRef<number | null>(null);
  const [viewerStatus, setViewerStatus] = useState('APS Viewer 준비 중');
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [levels, setLevels] = useState<any[]>([]);
  const [activeLevelIndex, setActiveLevelIndex] = useState<number | null>(null);
  const [activeLevelName, setActiveLevelName] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState<'orbit' | 'pov'>('orbit');
  const [povMessage, setPovMessage] = useState('층을 선택한 뒤 POV 시작을 누르면 내부 시점으로 진입합니다.');
  const [viewerAttempt, setViewerAttempt] = useState(0);
  const [facilityProfile, setFacilityProfile] = useState(() => BuildingConfigService.get());
  const [selectedBimBinding, setSelectedBimBinding] = useState<any | null>(null);
  const selectionHandlerRef = useRef<((event: any) => void) | null>(null);

  const modelBindings = [
    ...(facilityProfile.bindings?.spaces ?? []).map(binding => ({ ...binding, kind: 'space' as const })),
    ...(facilityProfile.bindings?.equipment ?? []).map(binding => ({ ...binding, kind: 'equipment' as const })),
  ].filter(binding => binding.modelUrn === modelUrn && Number.isFinite(Number(binding.objectId)));
  const activeRevitName = facilityProfile.models?.revit?.name || 'Revit 모델';

  const bindingReading = (binding: any, snapshot = DataBridge.getSnapshot()) => {
    const targetId = binding.kind === 'space' ? binding.canonicalSpaceId : binding.canonicalEquipmentId;
    const points = Object.values(facilityProfile.points ?? {}).filter(point =>
      binding.kind === 'space' ? point.spaceId === targetId : point.equipmentId === targetId);
    const preferred = points.sort((a, b) => {
      const priority = (role: string) => /temperature$/.test(role) ? 0 : /run_status|power/.test(role) ? 1 : 2;
      return priority(a.canonicalRole) - priority(b.canonicalRole);
    }).find(point => Number.isFinite(snapshot[point.sourcePointId]));
    if (!preferred) return null;
    return { point: preferred, value: Number(snapshot[preferred.sourcePointId]) };
  };

  const applyBimOverlay = (viewer = viewerRef.current, snapshot = DataBridge.getSnapshot()) => {
    if (!viewer) return;
    const vector = (x: number, y: number, z: number, w: number) => {
      const Vector = (window as any).THREE?.Vector4;
      return Vector ? new Vector(x, y, z, w) : { x, y, z, w };
    };
    viewer.clearThemingColors?.();
    for (const binding of modelBindings) {
      const reading = bindingReading(binding, snapshot);
      if (!reading) continue;
      const { value, point } = reading;
      let color = vector(0.15, 0.63, 0.47, 0.7);
      if (/temperature/.test(point.canonicalRole)) {
        color = value >= 28 ? vector(0.82, 0.18, 0.24, 0.78)
          : value >= 26 ? vector(0.9, 0.55, 0.08, 0.75)
            : value <= 18 ? vector(0.05, 0.55, 0.86, 0.75)
              : color;
      } else if (/run_status/.test(point.canonicalRole)) {
        color = value >= 0.5 ? color : vector(0.48, 0.52, 0.58, 0.55);
      }
      viewer.setThemingColor?.(Number(binding.objectId), color, undefined, true);
    }
    viewer.impl?.invalidate?.(true, true, true);
  };

  useEffect(() => BuildingConfigService.subscribe(() => setFacilityProfile({ ...BuildingConfigService.get() })), []);

  useEffect(() => {
    applyBimOverlay();
    return DataBridge.subscribeAll(snapshot => applyBimOverlay(viewerRef.current, snapshot));
  }, [facilityProfile, modelUrn]);

  const loadLevelsForViewer = useCallback(async (viewer: any) => {
    if (!viewer) return;
    try {
      const ext = viewer.getExtension?.('Autodesk.AEC.LevelsExtension')
        ?? await viewer.loadExtension?.('Autodesk.AEC.LevelsExtension');
      levelsExtRef.current = ext;
      const floorData = ext?.floorSelector?.floorData ?? [];
      if (floorData.length > 0) setLevels(floorData);
    } catch (error) {
      console.warn('[RevitModelReview] Levels extension failed', error);
    }
  }, []);

  useEffect(() => {
    let disposed = false;

    const delay = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

    const ensureSdk = () => new Promise<void>((resolve, reject) => {
      if ((window as any).Autodesk?.Viewing) {
        resolve();
        return;
      }
      if ((window as any).__AEES_APS_SDK_PROMISE__) {
        (window as any).__AEES_APS_SDK_PROMISE__.then(resolve).catch(reject);
        return;
      }
      if (!document.querySelector(`link[href^="${APS_VIEWER_STYLE_URL}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = APS_VIEWER_STYLE_URL;
        document.head.appendChild(link);
      }
      const existingScript = Array.from(document.scripts).find(script => script.src.includes('/modelderivative/v2/viewers/7.'));
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if ((window as any).Autodesk?.Viewing) resolve();
          else reject(new Error('Autodesk Viewer SDK는 로드됐지만 Autodesk 전역 객체를 찾지 못했습니다.'));
        }, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Autodesk Viewer SDK 로드에 실패했습니다.')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = `${APS_VIEWER_SCRIPT_URL}?aees_revit=${Date.now()}`;
      script.async = true;
      (window as any).__AEES_APS_SDK_PROMISE__ = new Promise<void>((sdkResolve, sdkReject) => {
        script.onload = () => {
          if ((window as any).Autodesk?.Viewing) sdkResolve();
          else sdkReject(new Error('Autodesk Viewer SDK가 로드됐지만 전역 객체를 찾지 못했습니다.'));
        };
        script.onerror = () => sdkReject(new Error('Autodesk Viewer SDK 로드에 실패했습니다.'));
      });
      (window as any).__AEES_APS_SDK_PROMISE__.then(resolve).catch((error: unknown) => {
        delete (window as any).__AEES_APS_SDK_PROMISE__;
        reject(error);
      });
      document.head.appendChild(script);
    });

    const releaseCanvasContext = (canvas: HTMLCanvasElement) => {
      try {
        const gl = (canvas.getContext('webgl2')
          || canvas.getContext('webgl')
          || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | WebGL2RenderingContext | null;
        const loseContext = gl?.getExtension?.('WEBGL_lose_context');
        loseContext?.loseContext?.();
      } catch {
        // Best-effort cleanup: the browser may already have lost the context.
      }
      try {
        canvas.width = 1;
        canvas.height = 1;
      } catch {
        // Ignore canvas size reset errors during recovery.
      }
    };

    const releaseContainerCanvases = (root: ParentNode | null = document) => {
      root?.querySelectorAll?.('canvas').forEach(canvas => {
        releaseCanvasContext(canvas as HTMLCanvasElement);
      });
    };

    const disposeViewer = (viewer: any) => {
      if (!viewer) return;
      try {
        const renderer = viewer.impl?.renderer?.();
        renderer?.forceContextLoss?.();
        renderer?.dispose?.();
      } catch {
        // Ignore APS renderer cleanup errors while recovering a WebGL context.
      }
      try {
        const context = viewer.impl?.glrenderer?.().context;
        context?.getExtension?.('WEBGL_lose_context')?.loseContext?.();
      } catch {
        // Ignore internal APS renderer differences across viewer versions.
      }
      try {
        viewer.tearDown?.();
      } catch {
        // Ignore APS cleanup errors during route transitions.
      }
      try {
        viewer.finish?.();
      } catch {
        // Ignore APS cleanup errors while recovering a WebGL context.
      }
    };

    const prepareViewerContainer = async () => {
      await delay(0);
      await new Promise(resolve => window.requestAnimationFrame(resolve));
      const container = viewerContainerRef.current;
      if (!container) throw new Error('APS Viewer 컨테이너를 찾지 못했습니다.');
      if (container.clientWidth < 20 || container.clientHeight < 20) {
        await delay(250);
      }
      if (container.clientWidth < 20 || container.clientHeight < 20) {
        throw new Error('APS Viewer 컨테이너 크기가 아직 준비되지 않았습니다.');
      }
      releaseContainerCanvases(container);
      container.replaceChildren();
      return container;
    };

    const createViewerWithRetry = async (attempt = 1): Promise<any> => {
      const previousViewer = (window as any).__AEES_APS_VIEWER__;
      if (previousViewer && previousViewer !== viewerRef.current) {
        disposeViewer(previousViewer);
        delete (window as any).__AEES_APS_VIEWER__;
        releaseContainerCanvases(document);
        await delay(500);
      }

      const container = await prepareViewerContainer();
      const viewer = new (window as any).Autodesk.Viewing.GuiViewer3D(container);
      const startedCode = viewer.start();
      if (startedCode > 0) {
        disposeViewer(viewer);
        releaseContainerCanvases(container);
        container.replaceChildren();
        releaseContainerCanvases(document);
        if (attempt < 5) {
          setViewerStatus(`Revit 모델 초기화 재시도 중 (${attempt + 1}/5)`);
          await delay(900 + attempt * 350);
          return createViewerWithRetry(attempt + 1);
        }
        if (startedCode === 10) {
          throw new Error('Viewer start failed: 10. Browser WebGL context limit was reached. The dashboard cleaned stale canvases; return to operational 3D or reload once if this keeps happening.');
        }
        throw new Error(`Viewer start failed: ${startedCode}. WebGL 컨텍스트를 정리한 뒤 다시 시도하세요.`);
      }

      viewer.setTheme('light-theme');
      viewer.setQualityLevel(false, true);
      viewer.setGhosting(true);
      viewerRef.current = viewer;
      (window as any).__AEES_APS_VIEWER__ = viewer;
      return viewer;
    };

    const focusViewerOnModel = async (viewer: any, source = 'focus') => {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        if (disposed) return false;
        if (attempt > 0) await delay(450);

        const models = [
          viewer.model,
          ...(viewer.getVisibleModels?.() ?? []),
          ...(viewer.impl?.modelQueue?.().getModels?.() ?? []),
        ].filter(Boolean);
        const model = models[0];
        if (!model) continue;

        let bounds: any = null;
        try {
          bounds = model.getBoundingBox?.();
        } catch {
          bounds = null;
        }

        const hasValidBounds = bounds
          && Number.isFinite(bounds.min?.x)
          && Number.isFinite(bounds.min?.y)
          && Number.isFinite(bounds.min?.z)
          && Number.isFinite(bounds.max?.x)
          && Number.isFinite(bounds.max?.y)
          && Number.isFinite(bounds.max?.z)
          && (
            Math.abs(bounds.max.x - bounds.min.x)
            + Math.abs(bounds.max.y - bounds.min.y)
            + Math.abs(bounds.max.z - bounds.min.z)
          ) > 0.001;

        try {
          viewer.setCutPlanes?.([]);
          viewer.clearSelection?.();
          viewer.explode?.(0);
          viewer.showAll?.();
          viewer.setDisplayEdges?.(true);
        } catch {
          // Best-effort visual reset.
        }

        if (hasValidBounds) {
          const THREE = (window as any).THREE ?? (window as any).Autodesk?.Viewing?.Private?.THREE;
          if (THREE && viewer.navigation) {
            const center = new THREE.Vector3(
              (bounds.min.x + bounds.max.x) / 2,
              (bounds.min.y + bounds.max.y) / 2,
              (bounds.min.z + bounds.max.z) / 2,
            );
            const spanX = Math.max(1, Math.abs(bounds.max.x - bounds.min.x));
            const spanY = Math.max(1, Math.abs(bounds.max.y - bounds.min.y));
            const spanZ = Math.max(1, Math.abs(bounds.max.z - bounds.min.z));
            const span = Math.max(spanX, spanY, spanZ);
            const eye = new THREE.Vector3(
              center.x - span * 0.82,
              center.y - span * 1.05,
              center.z + span * 0.62,
            );
            try {
              viewer.navigation.toPerspective?.();
              viewer.navigation.setCameraUpVector?.(new THREE.Vector3(0, 0, 1));
              viewer.navigation.setView?.(eye, center);
              viewer.navigation.setPosition?.(eye);
              viewer.navigation.setTarget?.(center);
            } catch {
              // Fit-to-view below is still enough for most APS states.
            }
          }
        }

        try {
          viewer.fitToView?.(undefined, model, true);
        } catch {
          try {
            viewer.fitToView?.();
          } catch {
            // Ignore fit failures and retry while the model settles.
          }
        }

        try {
          viewer.resize?.();
          viewer.impl?.invalidate?.(true, true, true);
        } catch {
          // Ignore render invalidation differences across APS versions.
        }

        if (hasValidBounds) {
          console.info(`[RevitModelReview] focused APS model from ${source}`);
          return true;
        }
      }
      return false;
    };

    const initializeViewer = async () => {
      try {
        setViewerError(null);
        setViewerReady(false);
        setViewerStatus('APS SDK 로딩 중');
        await ensureSdk();
        if (disposed || !viewerContainerRef.current) return;

        setViewerStatus('APS 토큰 요청 중');
        const tokenResponse = await fetch('/api/auth/token', { headers: AuthService.authHeaders() });
        if (!tokenResponse.ok) throw new Error(await tokenResponse.text());
        const tokenData = await tokenResponse.json();
        const accessToken = tokenData.access_token;

        setViewerStatus('Revit 모델 초기화 중');
        const options = {
          env: 'AutodeskProduction2',
          api: 'streamingV2',
          isAEC: true,
          getAccessToken: (onTokenReady: (token: string, expiresIn: number) => void) => {
            onTokenReady(accessToken, tokenData.expires_in ?? 3600);
          },
        };

        await new Promise<void>((resolve, reject) => {
          try {
            (window as any).Autodesk.Viewing.Initializer(options, () => {
              if (disposed || !viewerContainerRef.current) {
                resolve();
                return;
              }
              createViewerWithRetry().then(() => resolve()).catch(reject);
            });
          } catch (error) {
            reject(error);
          }
        });

        if (disposed || !viewerRef.current) return;
        const viewer = viewerRef.current;
        setViewerStatus(`${activeRevitName} 로딩 중`);

        const onGeometryLoaded = () => {
          if (disposed) return;
          void focusViewerOnModel(viewer, 'geometry-loaded');
          void loadLevelsForViewer(viewer);
          applyBimOverlay(viewer);
        };
        viewer.addEventListener((window as any).Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeometryLoaded);

        await new Promise<void>((resolve, reject) => {
          (window as any).Autodesk.Viewing.Document.load(
            `urn:${modelUrn}`,
            async (doc: any) => {
              try {
                const root = doc.getRoot();
                const viewables = root.search({ type: 'geometry', role: '3d' });
                const targetView = viewables.find((v: any) => v.data.name === '{3D}' || String(v.data.name).includes('3D'))
                  ?? root.getDefaultGeometry();

                try {
                  const aecLoader = (window as any).Autodesk?.Viewing?.Document?.getAecModelData;
                  const aecResult = typeof aecLoader === 'function'
                    ? aecLoader(targetView)
                    : doc.downloadAecModelData?.();
                  if (aecResult && typeof aecResult.then === 'function') await aecResult;
                } catch (aecError) {
                  console.warn('[RevitModelReview] AEC model data preload skipped', aecError);
                }

                await viewer.loadDocumentNode(doc, targetView);
                applyBimOverlay(viewer);
                const onSelectionChanged = (event: any) => {
                  const dbId = Number(event?.dbIdArray?.[0] ?? event?.selections?.[0]?.dbIdArray?.[0]);
                  setSelectedBimBinding(modelBindings.find(binding => Number(binding.objectId) === dbId) ?? null);
                };
                selectionHandlerRef.current = onSelectionChanged;
                viewer.addEventListener?.((window as any).Autodesk.Viewing.SELECTION_CHANGED_EVENT, onSelectionChanged);
                void focusViewerOnModel(viewer, 'document-loaded');
                resolve();
              } catch (error) {
                reject(error);
              }
            },
            (errorCode: unknown, errorMessage: unknown) => reject(new Error(`Document load failed: ${errorCode} ${errorMessage ?? ''}`)),
          );
        });

        if (disposed) return;
        setViewerReady(true);
        setViewerStatus('3D 모델 표시 완료');
        void focusViewerOnModel(viewer, 'ready');
        window.setTimeout(() => void focusViewerOnModel(viewer, 'late-1200'), 1200);
        window.setTimeout(() => void focusViewerOnModel(viewer, 'late-3000'), 3000);
        window.setTimeout(() => void loadLevelsForViewer(viewer), 800);
      } catch (error) {
        console.error('[RevitModelReview] APS viewer failed', error);
        if (!disposed) {
          setViewerError(error instanceof Error ? error.message : 'APS Viewer 초기화에 실패했습니다.');
          setViewerStatus('APS Viewer 오류');
        }
      }
    };

    initializeViewer();

    return () => {
      disposed = true;
      if (viewerRef.current) {
        if (selectionHandlerRef.current) {
          viewerRef.current.removeEventListener?.((window as any).Autodesk?.Viewing?.SELECTION_CHANGED_EVENT, selectionHandlerRef.current);
          selectionHandlerRef.current = null;
        }
        try {
          viewerRef.current.finish();
        } catch {
          // Ignore viewer cleanup errors during route transitions.
        }
        viewerRef.current = null;
      }
      releaseContainerCanvases(viewerContainerRef.current);
      if ((window as any).__AEES_APS_VIEWER__) delete (window as any).__AEES_APS_VIEWER__;
      if (povTimerRef.current) window.clearTimeout(povTimerRef.current);
    };
  }, [loadLevelsForViewer, modelUrn, viewerAttempt]);

  const firstFinite = (...values: unknown[]) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const numberValue = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(numberValue)) return numberValue;
    }
    return null;
  };

  const refitRevitModel = () => {
    const viewer = viewerRef.current;
    if (!viewer) {
      setViewerAttempt(prev => prev + 1);
      return;
    }

    try {
      viewer.setCutPlanes?.([]);
      viewer.clearSelection?.();
      viewer.explode?.(0);
      viewer.showAll?.();
      viewer.setDisplayEdges?.(true);
    } catch {
      // Best-effort APS visual reset.
    }

    const model = viewer.model ?? viewer.getVisibleModels?.()?.[0];
    try {
      const bounds = model?.getBoundingBox?.();
      const hasValidBounds = bounds
        && Number.isFinite(bounds.min?.x)
        && Number.isFinite(bounds.min?.y)
        && Number.isFinite(bounds.min?.z)
        && Number.isFinite(bounds.max?.x)
        && Number.isFinite(bounds.max?.y)
        && Number.isFinite(bounds.max?.z);
      const THREE = (window as any).THREE ?? (window as any).Autodesk?.Viewing?.Private?.THREE;
      if (hasValidBounds && THREE && viewer.navigation) {
        const center = new THREE.Vector3(
          (bounds.min.x + bounds.max.x) / 2,
          (bounds.min.y + bounds.max.y) / 2,
          (bounds.min.z + bounds.max.z) / 2,
        );
        const spanX = Math.max(1, Math.abs(bounds.max.x - bounds.min.x));
        const spanY = Math.max(1, Math.abs(bounds.max.y - bounds.min.y));
        const spanZ = Math.max(1, Math.abs(bounds.max.z - bounds.min.z));
        const span = Math.max(spanX, spanY, spanZ);
        const eye = new THREE.Vector3(
          center.x - span * 0.82,
          center.y - span * 1.05,
          center.z + span * 0.62,
        );
        viewer.navigation.toPerspective?.();
        viewer.navigation.setCameraUpVector?.(new THREE.Vector3(0, 0, 1));
        viewer.navigation.setView?.(eye, center);
        viewer.navigation.setPosition?.(eye);
        viewer.navigation.setTarget?.(center);
      }
    } catch {
      // Continue with APS native fit below.
    }

    try {
      viewer.fitToView?.(undefined, model, true);
    } catch {
      try {
        viewer.fitToView?.();
      } catch {
        // Ignore fit failures; invalidate below can still redraw the current camera.
      }
    }

    try {
      viewer.resize?.();
      viewer.impl?.invalidate?.(true, true, true);
    } catch {
      // Ignore APS version differences.
    }
  };

  const getPreferredPovLevelIndex = () => {
    if (activeLevelIndex !== null) return activeLevelIndex;
    const thirdFloorIndex = levels.findIndex(level => /3rd|3\s*f|3층|지상\s*3/i.test(String(level?.name ?? '')));
    if (thirdFloorIndex >= 0) return thirdFloorIndex;
    return levels.length > 0 ? levels.length - 1 : null;
  };

  const clickViewerToolByText = (needle: string) => {
    const viewer = viewerRef.current;
    const root = viewer?.container ?? viewerContainerRef.current ?? document;
    const controls = Array.from(root.querySelectorAll('.adsk-control, .adsk-button')) as HTMLElement[];
    const target = controls.find(control => (control.textContent ?? '').includes(needle));
    if (!target) return false;
    target.click();
    return true;
  };

  const dismissBimWalkIntro = () => {
    const roots = [viewerRef.current?.container, viewerContainerRef.current, document].filter(Boolean) as ParentNode[];
    const directSelectors = [
      '.c-bimwalk-tooltip__close',
      '.c-bimwalk-tooltip__footer button',
      '.c-bimwalk-tooltip button',
      '#tooltipPanel button',
      '[data-i18n*="bimwalk"]',
    ];

    for (const root of roots) {
      for (const selector of directSelectors) {
        const target = root.querySelector(selector) as HTMLElement | null;
        if (target) {
          target.click();
          return true;
        }
      }
    }

    const labels = ['다시 표시 안 함', '확인했음', '확인했음 다시 표시 안 함', "Don't show again", 'Got it', 'OK'];

    for (const label of labels) {
      for (const root of roots) {
        const controls = Array.from(root.querySelectorAll('button, label, input, .adsk-button')) as HTMLElement[];
        const target = controls.find(control => {
          const text = [
            control.textContent,
            control.getAttribute?.('aria-label'),
            control.getAttribute?.('title'),
            control.getAttribute?.('value'),
          ].filter(Boolean).join(' ');
          return text.includes(label) || label.includes(text);
        });
        if (target) {
          target.click();
          return true;
        }
      }
    }
    return false;
  };

  const deactivatePovTool = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    try {
      bimWalkExtRef.current?.deactivate?.();
      viewer.toolController?.deactivateTool?.('bimwalk');
      viewer.toolController?.deactivateTool?.('BimWalk');
      viewer.setActiveNavigationTool?.('freeorbit');
    } catch {
      // The APS viewer exposes slightly different navigation APIs by version.
    }
    clickViewerToolByText('자유 궤도');
  };

  const positionCameraForPov = (levelIndex: number | null) => {
    const viewer = viewerRef.current;
    const nav = viewer?.navigation;
    const THREE = (window as any).THREE;
    if (!viewer || !nav || !THREE) return false;

    const modelBounds = viewer.model?.getBoundingBox?.();
    const target = nav.getTarget?.();
    const center = target ?? (
      modelBounds
        ? new THREE.Vector3(
          (modelBounds.min.x + modelBounds.max.x) / 2,
          (modelBounds.min.y + modelBounds.max.y) / 2,
          (modelBounds.min.z + modelBounds.max.z) / 2,
        )
        : new THREE.Vector3(0, 0, 0)
    );
    const spanX = modelBounds ? Math.max(1, Math.abs(modelBounds.max.x - modelBounds.min.x)) : 30;
    const spanY = modelBounds ? Math.max(1, Math.abs(modelBounds.max.y - modelBounds.min.y)) : 30;
    const spanZ = modelBounds ? Math.max(1, Math.abs(modelBounds.max.z - modelBounds.min.z)) : 12;
    const level = levelIndex !== null ? levels[levelIndex] : null;
    const nextLevel = levelIndex !== null ? levels[levelIndex + 1] : null;
    const bottom = firstFinite(level?.zMin, level?.bottom, level?.elevation, level?.z, center.z - spanZ * 0.08);
    const top = firstFinite(level?.zMax, level?.top, nextLevel?.zMin, nextLevel?.elevation, bottom !== null ? bottom + spanZ / Math.max(levels.length, 3) : null);
    const floorHeight = bottom !== null && top !== null && top > bottom
      ? top - bottom
      : Math.max(3, spanZ / Math.max(levels.length, 3));
    const eyeZ = (bottom ?? center.z) + floorHeight * 0.45;
    const travel = Math.min(Math.max(Math.max(spanX, spanY) * 0.08, 6), 20);
    const eye = new THREE.Vector3(center.x - travel, center.y - travel * 0.65, eyeZ);
    const lookAt = new THREE.Vector3(center.x + travel * 0.45, center.y + travel * 0.28, eyeZ + floorHeight * 0.03);

    try {
      nav.toPerspective?.();
      nav.setCameraUpVector?.(new THREE.Vector3(0, 0, 1));
      nav.setVerticalFov?.(62, true);
      nav.setView?.(eye, lookAt);
      nav.setPosition?.(eye);
      nav.setTarget?.(lookAt);
      viewer.impl?.invalidate?.(true, true, true);
      return true;
    } catch (error) {
      console.warn('[RevitModelReview] Failed to place POV camera', error);
      return false;
    }
  };

  const activatePovTool = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return false;

    let activated = false;
    try {
      const extension = viewer.getExtension?.('Autodesk.BimWalk')
        ?? await viewer.loadExtension?.('Autodesk.BimWalk');
      bimWalkExtRef.current = extension;
      if (extension?.activate) {
        extension.activate();
        activated = true;
      }
    } catch (error) {
      console.warn('[RevitModelReview] BimWalk extension activation failed', error);
    }

    if (!activated) {
      try {
        const toolNames = viewer.toolController?.getToolNames?.() ?? [];
        const walkTool = toolNames.find((name: string) => /bim.?walk|first.?person|walk/i.test(String(name)));
        if (walkTool && viewer.toolController?.activateTool) {
          activated = viewer.toolController.activateTool(walkTool) !== false;
        }
      } catch {
        activated = false;
      }
    }

    if (!activated) activated = clickViewerToolByText('1인칭 시점');
    if (activated) {
      window.setTimeout(dismissBimWalkIntro, 250);
      window.setTimeout(dismissBimWalkIntro, 900);
      window.setTimeout(dismissBimWalkIntro, 1800);
    }
    return activated;
  };

  const applyLevelSelection = (index: number | null) => {
    const viewer = viewerRef.current;
    const floorSelector = levelsExtRef.current?.floorSelector;
    if (!viewer || !floorSelector) return;

    if (index === null) {
      floorSelector.selectFloor(undefined, true);
      viewer.showAll();
      viewer.clearSelection();
      viewer.isolate(0);
      viewer.setCutPlanes([]);
      viewer.fitToView();
      setActiveLevelIndex(null);
      setActiveLevelName(null);
      return;
    }

    floorSelector.selectFloor(index, true);
    setActiveLevelIndex(index);
    setActiveLevelName(levels[index]?.name ?? null);
  };

  const schedulePovEntry = (levelIndex: number) => {
    if (povTimerRef.current) window.clearTimeout(povTimerRef.current);
    setPovMessage(`${levels[levelIndex]?.name ?? '선택층'} 내부 시점으로 이동 중입니다.`);
    povTimerRef.current = window.setTimeout(async () => {
      const positioned = positionCameraForPov(levelIndex);
      const activated = await activatePovTool();
      setPovMessage(
        activated
          ? 'POV 활성화: WASD/방향키 이동 · 마우스 드래그 시선 전환 · 궤도 보기로 복귀'
          : positioned
            ? '내부 시점으로 이동했습니다. APS 툴바의 1인칭 시점을 눌러 이동할 수 있습니다.'
            : 'POV 진입 위치를 계산하지 못했습니다. 층을 다시 선택한 뒤 시도하세요.',
      );
    }, 520);
  };

  const enterPovMode = () => {
    const levelIndex = getPreferredPovLevelIndex();
    if (levelIndex === null) {
      setPovMessage('POV를 시작하려면 먼저 APS 레벨 데이터가 로드되어야 합니다.');
      return;
    }
    setReviewMode('pov');
    applyLevelSelection(levelIndex);
    schedulePovEntry(levelIndex);
  };

  const exitPovMode = () => {
    if (povTimerRef.current) window.clearTimeout(povTimerRef.current);
    deactivatePovTool();
    setReviewMode('orbit');
    setPovMessage('궤도 검토 모드입니다. 층을 선택한 뒤 POV 시작으로 내부에 진입할 수 있습니다.');
    const viewer = viewerRef.current;
    if (viewer) {
      viewer.navigation?.toPerspective?.();
      viewer.fitToView?.();
    }
  };

  const selectLevel = (index: number | null) => {
    if (index === null) {
      exitPovMode();
      applyLevelSelection(null);
      return;
    }
    applyLevelSelection(index);
    if (reviewMode === 'pov') schedulePovEntry(index);
  };

  const sortedLevels = levels.map((level, index) => ({ level, index })).reverse();

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#e8eef1]">
      <div ref={viewerContainerRef} className="h-full w-full" />

      <header className="absolute inset-x-0 top-0 z-[60] flex min-h-[78px] items-center justify-between gap-4 border-b border-[#dfe3e6] bg-white/96 px-4 shadow-[0_8px_24px_rgba(17,24,39,0.04)] backdrop-blur-md md:pl-28 lg:px-6 lg:pl-28">
        <div className="flex min-w-0 items-center gap-3">
          <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#dfe3e6] bg-[#f7f8f8] text-[#08777d] sm:flex">
            <i className="fa-solid fa-cubes text-sm" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${viewerReady ? 'bg-[#167452]' : 'bg-[#d6a44a]'}`} />
              <h1 className="truncate text-base font-semibold tracking-[-0.02em] text-[#171a20]">Revit 모델 검토</h1>
              <span className="hidden rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-2 py-0.5 text-[10px] font-bold text-[#08777d] sm:inline-flex">APS Viewer</span>
            </div>
            <p className="mt-1 truncate text-[11px] text-[#70767d]">{activeRevitName} · {viewerReady ? '검토 가능' : viewerStatus}</p>
          </div>
        </div>
        <ModelWorkspaceSelector
          mode="revit"
          revitAvailable
          onSelect={target => {
            if (target === 'operational') onViewerModeChange('operational');
            if (target === 'settings') onOpenSettings();
          }}
        />
      </header>

      {(!viewerReady || viewerError) && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/70 text-[#08777d] backdrop-blur">
          <div className="max-w-md rounded-2xl border border-[#143746]/15 bg-white px-6 py-5 text-center shadow-[0_18px_45px_rgba(15,35,45,0.12)]">
            {viewerError ? (
              <>
                <p className="font-orbitron text-[10px] font-bold uppercase tracking-[0.22em] text-[#bd4655]">APS VIEWER ERROR</p>
                <p className="mt-2 text-sm font-bold text-[#142b38]">{viewerError}</p>
                <p className="mt-2 text-xs leading-relaxed text-[#4e6572]">Autodesk Viewer CDN, APS 토큰, 모델 URN 상태를 확인하세요.</p>
                <button
                  type="button"
                  onClick={() => {
                    setViewerError(null);
                    setViewerReady(false);
                    setViewerAttempt(value => value + 1);
                  }}
                  className="mt-4 h-9 rounded-xl bg-[#0f8b8d] px-4 text-xs font-bold text-white shadow-[0_8px_20px_rgba(15,139,141,0.22)] transition hover:bg-[#08777d]"
                >
                  뷰어 다시 시작
                </button>
              </>
            ) : (
              <>
                <div className="mx-auto h-10 w-10 rounded-full border-2 border-[#0f8b8d]/20 border-t-[#0f8b8d] animate-spin" />
                <p className="mt-3 font-orbitron text-[10px] font-bold uppercase tracking-[0.22em] text-[#08777d]">REVIT VIEWER LOADING</p>
                <p className="mt-2 text-sm font-bold text-[#142b38]">{viewerStatus}</p>
              </>
            )}
          </div>
        </div>
      )}

      {levels.length > 0 && (
        <div className="absolute left-4 bottom-28 z-50 flex flex-col gap-2 md:left-32">
          <div className="ml-1 font-orbitron text-[9px] font-bold uppercase tracking-widest text-[#4e6572]">Floor Level</div>
          <div className="flex flex-col gap-1 rounded-2xl border border-[#143746]/15 bg-white/[0.92] p-1.5 shadow-[0_18px_45px_rgba(15,35,45,0.12)] backdrop-blur">
            <button
              onClick={() => selectLevel(null)}
              className={`h-10 min-w-12 rounded-xl px-2 font-orbitron text-[10px] font-bold transition ${activeLevelIndex === null ? 'bg-[#0f8b8d] text-white' : 'text-[#4e6572] hover:bg-[#edf4f6] hover:text-[#08777d]'}`}
            >
              ALL
            </button>
            {sortedLevels.map(({ level, index }) => (
              <button
                key={`${level.name}-${index}`}
                onClick={() => selectLevel(index)}
                className={`h-10 min-w-12 rounded-xl px-2 font-orbitron text-[10px] font-bold transition ${activeLevelIndex === index ? 'bg-[#0f8b8d] text-white' : 'text-[#4e6572] hover:bg-[#edf4f6] hover:text-[#08777d]'}`}
                title={level.name}
              >
                {String(level.name).replace('Level ', '').replace('FL', 'F')}
              </button>
            ))}
          </div>
        </div>
      )}

      {reviewMode === 'pov' && (
        <div className="pointer-events-none absolute left-1/2 top-24 z-50 -translate-x-1/2 rounded-2xl border border-[#c7d5d6] bg-white/95 px-4 py-2 text-center shadow-[0_14px_34px_rgba(17,24,39,0.12)] backdrop-blur">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#08777d]">REVIT POV WALKTHROUGH</p>
          <p className="mt-0.5 text-[11px] text-[#4f5962]">WASD/방향키 이동 · 마우스 드래그 시선 전환 · 궤도 보기로 복귀</p>
        </div>
      )}

      <div className="pointer-events-auto absolute right-4 top-24 z-50 w-[300px] rounded-2xl border border-[#143746]/15 bg-white/[0.94] p-4 shadow-[0_18px_45px_rgba(15,35,45,0.12)] backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#08777d]">BIM SENSOR OVERLAY</p>
            <p className="mt-1 text-xs font-semibold text-[#142b38]">프로필 바인딩 {modelBindings.length}개</p>
          </div>
          <button type="button" onClick={() => applyBimOverlay()} className="h-8 rounded-lg border border-[#0f8b8d]/20 px-2.5 text-[10px] font-bold text-[#08777d] hover:bg-[#edf4f6]">
            <i className="fa-solid fa-rotate mr-1" />갱신
          </button>
        </div>
        {modelBindings.length === 0 ? (
          <p className="mt-3 rounded-xl bg-[#f3f6f7] px-3 py-2 text-[10px] leading-relaxed text-[#66737b]">설정 → BIM 연결에서 Revit 객체와 공간·설비를 먼저 연결하세요.</p>
        ) : selectedBimBinding ? (() => {
          const reading = bindingReading(selectedBimBinding);
          return <div className="mt-3 rounded-xl border border-[#0f8b8d]/15 bg-[#edf6f6] p-3">
            <p className="truncate text-xs font-bold text-[#142b38]">{selectedBimBinding.name}</p>
            <p className="mt-1 text-[9px] text-[#68777f]">{selectedBimBinding.kind === 'space' ? '공간' : '설비'} · Object {selectedBimBinding.objectId}</p>
            <p className="mt-2 text-sm font-bold text-[#08777d]">{reading ? `${reading.value.toFixed(1)} ${reading.point.unit || ''}` : '연결 센서값 없음'}</p>
            {reading && <p className="mt-0.5 truncate text-[9px] text-[#68777f]">{reading.point.label}</p>}
          </div>;
        })() : (
          <p className="mt-3 text-[10px] leading-relaxed text-[#66737b]">색상으로 센서 상태를 표시합니다. 연결된 객체를 선택하면 대표 센서값을 확인할 수 있습니다.</p>
        )}
      </div>

      <div className="pointer-events-auto absolute left-4 top-24 z-50 max-w-[390px] rounded-2xl border border-[#143746]/15 bg-white/[0.92] px-5 py-4 shadow-[0_18px_45px_rgba(15,35,45,0.12)] backdrop-blur md:left-32">
        <p className="font-orbitron text-[10px] font-bold uppercase tracking-[0.28em] text-[#08777d]">REVIT MODEL REVIEW</p>
        <h2 className="mt-1 text-base font-bold text-[#142b38]">{activeRevitName}</h2>
        <p className="mt-1 text-xs leading-relaxed text-[#4e6572]">
          시설 프로필에 연결된 Revit 변환본입니다. 층을 선택한 뒤 POV로 진입해 공간과 BIM 센서 바인딩을 검토하세요.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold text-[#08777d]">
          <span className="rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-3 py-1">SVF2 변환 완료</span>
          <span className="rounded-full border border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08] px-3 py-1">
            {activeLevelName ? `${activeLevelName} 선택` : '전체 모델'}
          </span>
          <span className={`rounded-full border px-3 py-1 ${reviewMode === 'pov' ? 'border-[#0f8b8d]/35 bg-[#0f8b8d]/[0.14]' : 'border-[#0f8b8d]/20 bg-[#0f8b8d]/[0.08]'}`}>
            {reviewMode === 'pov' ? 'POV 활성' : '궤도 검토'}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={enterPovMode}
            disabled={!viewerReady || levels.length === 0}
            className={`h-9 rounded-xl text-xs font-bold transition ${reviewMode === 'pov' ? 'bg-[#0f8b8d] text-white shadow-[0_8px_20px_rgba(15,139,141,0.22)]' : 'bg-[#0f8b8d] text-white hover:bg-[#08777d] disabled:cursor-not-allowed disabled:opacity-45'}`}
          >
            POV 시작
          </button>
          <button
            type="button"
            onClick={exitPovMode}
            className="h-9 rounded-xl border border-[#c7d5d6] bg-white text-xs font-bold text-[#08777d] transition hover:bg-[#edf7f7]"
          >
            궤도 보기
          </button>
          <button
            type="button"
            onClick={refitRevitModel}
            disabled={!viewerReady}
            className="h-9 rounded-xl border border-[#c7d5d6] bg-white text-xs font-bold text-[#08777d] transition hover:bg-[#edf7f7] disabled:cursor-not-allowed disabled:opacity-45"
          >
            화면 맞춤
          </button>
        </div>
        <p className="mt-2 text-[11px] font-medium leading-relaxed text-[#4e6572]">
          {povMessage}
        </p>
        <div className="mt-2 rounded-xl border border-[#dfe9ea] bg-[#f7fbfb] px-3 py-2 text-[10px] leading-relaxed text-[#62717a]">
          {activeRevitName} 변환본 검토용입니다. 층, POV 동선과 BIM 센서 바인딩 상태를 확인합니다.
        </div>
      </div>

    </div>
  );
};

const EP_STEP_SECONDS = 20 * 60;
const AUTO_VALIDATION_DATE = '20180601';
const AUTO_VALIDATION_END_DATE = '20180831';
const AUTO_VALIDATION_SECONDS = HVAC_OPERATION_START_SECONDS;
const AUTO_VALIDATION_SPEED = 10;
const AUTO_VALIDATION_EP_STEPS = Math.floor((HISTORICAL_TEST_WINDOW_END_SECONDS - AUTO_VALIDATION_SECONDS) / EP_STEP_SECONDS);

const normalizeTrackingMessage = (
  message: unknown,
  fallback: string,
  started = false,
) => {
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text) return fallback;
  const lower = text.toLowerCase();
  if (lower.includes('already') && lower.includes('running')) return 'Tracking Twin이 이미 실행 중입니다. 현재 실행을 유지합니다.';
  if (lower.includes('started')) return 'Tracking Twin을 시작했습니다.';
  if (lower.includes('failed')) return 'Tracking Twin 시작에 실패했습니다.';
  if (text.includes('/twin/tracking/stop')) return 'Tracking Twin이 이미 실행 중입니다. 중지가 필요하면 Tracking 중지를 먼저 실행하세요.';
  if (started) return text;
  return text.includes('?') ? fallback : text;
};

function buildHistoricalDateRange(startDate: string, endDate: string) {
  const toDate = (value: string) => new Date(
    Number(value.slice(0, 4)),
    Number(value.slice(4, 6)) - 1,
    Number(value.slice(6, 8)),
  );
  const dates: string[] = [];
  const cursor = toDate(startDate);
  const end = toDate(endDate);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    dates.push(`${y}${m}${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

const AUTO_VALIDATION_DATES = buildHistoricalDateRange(AUTO_VALIDATION_DATE, AUTO_VALIDATION_END_DATE);

BuildingConfigService.load().finally(() => {
    BMSSimulationService.start(2000);
    DataBridge.start();          // Edge → Platform 추상화 계층 시작
    TwinContextService.initialize();
});

const averageFinite = (values: Array<number | undefined>) => {
  const finite = values.filter((value): value is number => Number.isFinite(value));
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null;
};

type MeasuredHvacState = {
  power: number | null;
  systemOn: boolean | null;
};

const historicalHvacCache = new Map<string, Promise<Array<{
  seconds: number;
  hvac_kw?: number | null;
  system_on?: boolean | null;
}>>>();

type TrackingPrefillRequest = {
  date?: string;
  seconds?: number;
  autoApplySetpoints?: boolean;
  autoStartTracking?: boolean;
  source?: string;
  trackingOptions?: {
    disable_throttle?: boolean;
    max_steps?: number;
    stop_after_epoch?: number;
    start_epoch?: number;
    run_period_days?: number;
    require_ready?: boolean;
    allow_system_off_start?: boolean;
  };
  skipDigitalTwinHandler?: boolean;
};

const normalizeHistoricalDate = (dateValue: string) => dateValue.replaceAll('-', '');

const LIVE_HVAC_KW_POINTS = [
  'BEMS.HVAC.KW',
  'HVAC.POWER.KW',
  'POWER.HVAC.KW',
  'POWER.WHM.HVAC_KW',
];

const LIVE_SYSTEM_ON_POINTS = [
  'BEMS.HVAC.SYSTEM_ON',
  'HVAC.SYSTEM_ON',
  'SYSTEM.HVAC.ON',
];

const finiteSnapshotValue = (snapshot: Record<string, number>, pointIds: string[]) => {
  for (const pointId of pointIds) {
    const value = snapshot[pointId];
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const getLiveMeasuredHvacState = (frame: TwinFrame): MeasuredHvacState | null => {
  if (frame.source !== 'live-measured') return null;
  const power = finiteSnapshotValue(frame.snapshot, LIVE_HVAC_KW_POINTS);
  const onValue = finiteSnapshotValue(frame.snapshot, LIVE_SYSTEM_ON_POINTS);
  const systemOn = onValue != null ? onValue > 0.5 : (power != null ? power > 1.0 : null);
  return { power, systemOn };
};

const getMeasuredHvacStateAt = async (
  dateValue: string,
  seconds: number,
): Promise<MeasuredHvacState> => {
  const date = normalizeHistoricalDate(dateValue);
  const bucket = Math.floor(seconds / 1200) * 1200;
  try {
    let stepsPromise = historicalHvacCache.get(date);
    if (!stepsPromise) {
      stepsPromise = fetch(`/api/historical/resampled?date=${date}&step=1200`)
        .then(async response => {
          if (!response.ok) throw new Error(`Historical HVAC request failed: ${response.status}`);
          const payload = await response.json();
          return payload.steps ?? [];
        });
      historicalHvacCache.set(date, stepsPromise);
    }
    const steps = await stepsPromise;
    const step = steps.find(item => item.seconds === bucket);
    return {
      power: step?.hvac_kw ?? null,
      systemOn: step?.system_on ?? null,
    };
  } catch {
    historicalHvacCache.delete(date);
    return { power: null, systemOn: null };
  }
};

const getMeasuredHvacState = async (frame: TwinFrame): Promise<MeasuredHvacState> => {
  if (frame.source === 'energyplus') {
    const power = finiteSnapshotValue(frame.snapshot, ['EPLUS.POWER.BEMS_COMPARABLE_KW']);
    const onValue = finiteSnapshotValue(frame.snapshot, ['EPLUS.STATUS.SYSTEM_ON']);
    return { power, systemOn: onValue != null ? onValue > 0.5 : power != null ? power > 1 : null };
  }
  const liveState = getLiveMeasuredHvacState(frame);
  if (liveState) return liveState;
  if (!frame.date || !frame.time) return { power: null, systemOn: null };
  const [hours, minutes, seconds = 0] = frame.time.split(':').map(Number);
  return getMeasuredHvacStateAt(frame.date, (hours * 3600) + (minutes * 60) + seconds);
};

const secondsToHms = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(value => String(value).padStart(2, '0')).join(':');
};

const historicalEpochFor = (date: string, seconds: number) => {
  const normalizedDate = normalizeHistoricalDate(date);
  const yr = parseInt(normalizedDate.slice(0, 4), 10);
  const mo = parseInt(normalizedDate.slice(4, 6), 10) - 1;
  const dy = parseInt(normalizedDate.slice(6, 8), 10);
  return new Date(yr, mo, dy, 0, 0, seconds).getTime() / 1000;
};

const fetchHistoricalSnapshotAt = async (date: string, seconds: number): Promise<Record<string, number>> => {
  const response = await fetch(`/api/historical/snapshot?date=${normalizeHistoricalDate(date)}&time=${secondsToHms(seconds)}`);
  if (!response.ok) throw new Error(`Historical snapshot request failed: ${response.status}`);
  const payload = await response.json();
  return payload.data ?? {};
};

const frameToSensorData = (frame: TwinFrame, power: number | null): SensorData => {
  const raw = frame.snapshot;
  const temp = averageFinite([
    raw['DDC_M.D.D0116'], raw['DDC_M.D.D0117'], raw['DDC_M.D.D0118'], raw['DDC_M.D.D0119'],
    raw['DDC_M.D.D0120'], raw['DDC_M.D.D0121'], raw['DDC_M.D.D0122'], raw['DDC_M.D.D0123'],
  ]) ?? 0;
  const comfortProxy = Math.max(-3, Math.min(3, (temp - 26) / 3));
  return {
    temp,
    humidity: raw['AHU.D4000'] ?? 0,
    pmv: comfortProxy,
    ppd: Math.min(100, Math.abs(comfortProxy) * 30),
    power: power ?? 0,
    energySaving: 0,
    outdoorTemp: raw['DDC_G.D.D0100'] ?? 0,
    outdoorHumidity: raw['DDC_G.D.D0151'] ?? 0,
    windSpeed: 0,
    aqi: 0,
    co2: averageFinite([
      raw['DDC_G.D.D0152'], raw['DDC_G.D.D0153'], raw['DDC_G.D.D0154'],
      raw['DDC_G.D.D0155'], raw['DDC_G.D.D0158'], raw['DDC_G.D.D0159'], raw['DDC_G.D.D0160'],
    ]) ?? 0,
    lux: 0,
    toe: 0,
    energyIntensity: 0,
  };
};

const App: React.FC = () => {
  const [authUser, setAuthUser]     = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [preferredDataSource, setPreferredDataSource] = useState<DataSourceMode>(() => {
    const mode = getPreferredDataSourceMode();
    return mode === 'simulation' ? 'historical' : mode;
  });

  useEffect(() => {
    const syncPreferredDataSource = () => {
      const mode = getPreferredDataSourceMode();
      setPreferredDataSource(mode === 'simulation' ? 'historical' : mode);
    };
    window.addEventListener('aees:data-source-preference', syncPreferredDataSource);
    window.addEventListener('storage', syncPreferredDataSource);
    return () => {
      window.removeEventListener('aees:data-source-preference', syncPreferredDataSource);
      window.removeEventListener('storage', syncPreferredDataSource);
    };
  }, []);

  // 앱 시작 시 기존 세션 검증
  useEffect(() => {
    AuthService.validateSession().then(user => {
      setAuthUser(user);
      setAuthChecked(true);
    });
  }, []);

  // The server safety runtime is authoritative across browser sessions and
  // restarts. Reconcile the local selector after login so the header never
  // claims EnergyPlus/live while the backend is operating another source.
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    void fetch('/api/system-control/runtime', { cache: 'no-store' })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || '제어 런타임 조회 실패');
        const sourceMode = payload?.runtime?.sourceMode;
        if (!cancelled && ['historical', 'energyplus', 'live'].includes(sourceMode)) {
          setPreferredDataSourceMode(sourceMode as DataSourceMode);
        }
      })
      .catch(error => console.warn('Server data source synchronization failed.', error));
    return () => { cancelled = true; };
  }, [authUser]);

  const handleLogout = async () => {
    await AuthService.logout();
    setAuthUser(null);
  };

  const [activeTab, setActiveTab] = useState<string>(() => readPersistedTab());
  const [guidedDemoOpen, setGuidedDemoOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('guidedDemo') === '1';
  });
  const [overviewInitialCameraMode, setOverviewInitialCameraMode] = useState<'orbit' | 'section'>('orbit');
  const [home3DReady, setHome3DReady] = useState(false);
  const [controlMode, setControlMode] = useState<ControlMode>(() => readPersistedControlMode());
  const [sensorData, setSensorData] = useState<SensorData>({
    temp: 0, humidity: 0, pmv: 0, ppd: 0, power: 0, energySaving: 0,
    outdoorTemp: 0, outdoorHumidity: 0, windSpeed: 0, aqi: 0,
    co2: 0, lux: 0, toe: 0, energyIntensity: 0,
  });

  const handleControlModeChange = useCallback((mode: ControlMode) => {
    const previous = controlMode;
    setControlMode(mode);
    if (preferredDataSource !== 'energyplus') return;
    const operatingMode: Record<ControlMode, EnergyPlusOperatingMode> = {
      [ControlMode.MANUAL]: 'manual',
      [ControlMode.AI_AUTO]: 'automatic',
      [ControlMode.DECISION_SUPPORT]: 'advisory',
    };
    void EnergyPlusTestbedService.setOperatingMode(operatingMode[mode]).catch(error => {
      console.error('[AEES] EnergyPlus operating mode update failed', error);
      setControlMode(previous);
      window.dispatchEvent(new CustomEvent('aees:control-mode-error', {
        detail: { message: error instanceof Error ? error.message : '운영 모드를 변경하지 못했습니다.' },
      }));
    });
  }, [controlMode, preferredDataSource]);

  useEffect(() => EnergyPlusTestbedService.subscribe(status => {
    if (preferredDataSource !== 'energyplus' || !status.operating_mode) return;
    const mode: Record<EnergyPlusOperatingMode, ControlMode> = {
      manual: ControlMode.MANUAL,
      automatic: ControlMode.AI_AUTO,
      advisory: ControlMode.DECISION_SUPPORT,
    };
    setControlMode(mode[status.operating_mode]);
  }), [preferredDataSource]);

  const handleDataSourceModeChange = useCallback(async (nextMode: DataSourceMode) => {
    if (nextMode === preferredDataSource) return;
    if (preferredDataSource === 'energyplus' && nextMode !== 'energyplus') {
      await Promise.allSettled([
        fetch('/api/eplus-testbed/session/stop', {
          method: 'POST',
          headers: AuthService.authHeaders(),
        }),
        fetch('/api/twin/tracking/stop', {
          method: 'POST',
          headers: AuthService.authHeaders(),
        }),
      ]);
    }
    if (nextMode !== 'historical') HistoricalService.stop();
    const runtimeSource = nextMode === 'energyplus' ? 'energyplus' : nextMode === 'live' ? 'live' : 'historical';
    const runtimeResponse = await fetch('/api/system-control/runtime/transition', {
      method: 'POST',
      headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        stage: 'monitoring',
        sourceMode: runtimeSource,
        actor: 'dashboard-operator',
        reason: `대시보드 데이터 소스 전환 · ${runtimeSource}`,
      }),
    });
    const runtimePayload = await runtimeResponse.json().catch(() => ({}));
    if (!runtimeResponse.ok) {
      throw new Error(runtimePayload.message || '서버 제어 런타임의 데이터 소스를 변경하지 못했습니다.');
    }
    setPreferredDataSourceMode(nextMode);
  }, [preferredDataSource]);

  const [modelUrn, setModelUrnState] = useState<string>(() => readStorage(ACTIVE_MODEL_STORAGE_KEY));
  const [preferredRevitUrn, setPreferredRevitUrn] = useState<string>('');
  const [homeViewerMode, setHomeViewerModeState] = useState<HomeViewerMode>('operational');
  const activeFacilityConfig = BuildingConfigService.get();
  const activeFacilityId = String(activeFacilityConfig.profile?.facilityId || activeFacilityConfig.meta.id || '').toLowerCase();
  const profileRevitUrn = activeFacilityConfig.models?.revit?.status === 'ready'
    ? activeFacilityConfig.models.revit.urn || ''
    : '';
  // A saved/model-list SEDC URN is only a compatibility fallback for sedc-01.
  // Other facilities must explicitly bind their own Revit model in the active profile.
  const reviewModelUrn = profileRevitUrn || (activeFacilityId === 'sedc-01' ? preferredRevitUrn || modelUrn : '');

  const setModelUrn = useCallback((urn: string) => {
    setModelUrnState(urn);
    writeStorage(ACTIVE_MODEL_STORAGE_KEY, urn);
  }, []);

  useEffect(() => {
    const syncProfileModel = () => {
      const revit = BuildingConfigService.get().models?.revit;
      if (revit?.status === 'ready' && revit.urn) {
        setPreferredRevitUrn(revit.urn);
        setModelUrn(revit.urn);
      } else {
        setPreferredRevitUrn('');
      }
    };
    syncProfileModel();
    return BuildingConfigService.subscribe(syncProfileModel);
  }, [setModelUrn]);

  const setHomeViewerMode = useCallback((mode: HomeViewerMode) => {
    setHomeViewerModeState(mode);
    // Avoid auto-starting APS Viewer after refresh; stale WebGL contexts are the most common
    // cause of viewer.start() code 10 during Vite/HMR testing.
    writeStorage(HOME_VIEWER_MODE_STORAGE_KEY, mode === 'operational' ? mode : 'operational');
  }, []);

  const handleWorkspaceSelect = useCallback((target: ModelWorkspaceTarget) => {
    if (target === 'settings') {
      setActiveTab('settings');
      return;
    }
    if (target === 'revit') {
      if (reviewModelUrn) {
        setHomeViewerMode('revit-prep');
      } else {
        setActiveTab('settings');
      }
      return;
    }
    setHomeViewerMode('operational');
  }, [reviewModelUrn, setHomeViewerMode]);

  useEffect(() => {
    writeStorage(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    writeStorage(CONTROL_MODE_STORAGE_KEY, controlMode);
  }, [controlMode]);

  useEffect(() => {
    if (homeViewerMode !== 'revit-prep') return;
    const timer = window.setTimeout(() => {
      setHomeViewerModeState('revit');
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [homeViewerMode]);

  useEffect(() => {
    if (!authUser || activeTab !== 'overview' || home3DReady) return;

    const browserWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;

    if (browserWindow.requestIdleCallback) {
      idleHandle = browserWindow.requestIdleCallback(() => setHome3DReady(true), { timeout: 1200 });
    } else {
      timeoutHandle = window.setTimeout(() => setHome3DReady(true), 600);
    }

    return () => {
      if (idleHandle != null) browserWindow.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle != null) window.clearTimeout(timeoutHandle);
    };
  }, [activeTab, authUser, home3DReady]);

  // One shared BEMS frame drives the global context and copilot.
  useEffect(() => {
    let requestVersion = 0;
    return TwinContextService.subscribe(async frame => {
      const version = ++requestVersion;
      const { power } = await getMeasuredHvacState(frame);
      if (version !== requestVersion) return;
      setSensorData(frameToSensorData(frame, power));
    });
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setOverviewInitialCameraMode('orbit');
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (!guidedDemoOpen || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('guidedDemo')) return;
    url.searchParams.delete('guidedDemo');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [guidedDemoOpen]);

  const handleFloorInfoBack = useCallback(() => {
    setOverviewInitialCameraMode('section');
    setActiveTab('overview');
  }, []);

  // ── DataBuffer 주입 헬퍼 ────────────────────────────────────────────────────
  const pushBmsToBuffer = useCallback(async (
    timestamp?: number,
    historicalPosition?: { date: string; seconds: number },
    snapshotOverride?: Record<string, number>,
  ) => {
    try {
      const frame = TwinContextService.getFrame();
      const snap = snapshotOverride ?? frame.snapshot;
      const measured = historicalPosition
        ? await getMeasuredHvacStateAt(historicalPosition.date, historicalPosition.seconds)
        : await getMeasuredHvacState(frame);
      const historicalSystemOn = historicalPosition
        ? historicalPosition.seconds >= 8 * 3600 && historicalPosition.seconds < 18 * 3600
        : null;
      const systemOn = measured.systemOn
        ?? (measured.power != null ? measured.power > 1.0 : historicalSystemOn);
      const zoneTemps = Object.fromEntries(
        [
          'DDC_M.D.D0116',
          'DDC_M.D.D0117',
          'DDC_M.D.D0118',
          'DDC_M.D.D0119',
        ]
          .map(pointId => [pointId, snap[pointId]] as const)
          .filter((entry): entry is readonly [string, number] => Number.isFinite(entry[1])),
      );
      const response = await fetch('/api/twin/bms-inject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp:    timestamp,   // Historical: 2018 epoch / Live: undefined → server uses time.time()
          outdoor_temp: snap['DDC_G.D.D0100'] ?? null,
          outdoor_rh:   snap['DDC_G.D.D0151'] ?? null,
          zone_temps: zoneTemps,
          facility_kw: measured.power,
          cooling_kw:  measured.power,
          ahu_sa_temp: snap['AHU.D4002'] ?? null,
          ahu_ra_temp: snap['AHU.D4001'] ?? null,
          ahu_ra_temp_sp: snap['AHU.D4021'] ?? null,
          chw_supply:  snap['DDC_M.D.D0103'] ?? null,
          bt5_temp:    snap['DDC_M.D.D0107'] ?? null,
          system_on:   systemOn,
        }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const prefillTrackingWindow = useCallback(async (date: string, seconds: number) => {
    const stepSec = 30;
    const windowSec = 20 * 60;
    const startSeconds = Math.max(0, seconds - windowSec + stepSec);
    const sampleSeconds: number[] = [];
    for (let sec = startSeconds; sec <= seconds; sec += stepSec) {
      sampleSeconds.push(sec);
    }

    let injected = 0;
    for (let idx = 0; idx < sampleSeconds.length; idx += 8) {
      const batch = sampleSeconds.slice(idx, idx + 8);
      const snapshots = await Promise.all(batch.map(sec => fetchHistoricalSnapshotAt(date, sec)));
      for (let batchIndex = 0; batchIndex < batch.length; batchIndex += 1) {
        const sec = batch[batchIndex];
        const ok = await pushBmsToBuffer(
          historicalEpochFor(date, sec),
          { date, seconds: sec },
          snapshots[batchIndex],
        );
        if (ok) injected += 1;
      }
    }

    const response = await fetch('/api/twin/tracking/status');
    const status = response.ok ? await response.json() : null;
    return {
      injected,
      requested: sampleSeconds.length,
      ready: Boolean(status?.buffer?.bems_time?.ready),
      detail: status?.buffer?.bems_time?.detail ?? '',
    };
  }, [pushBmsToBuffer]);

  useEffect(() => {
    const handlePrefill = async (event: Event) => {
      const status = HistoricalService.getStatus();
      const detail = (event as CustomEvent<TrackingPrefillRequest>).detail ?? {};
      const wasPlayingBeforePrefill = HistoricalService.isPlaying();
      const shouldResumeAfterPrefill = Boolean(detail.autoStartTracking && wasPlayingBeforePrefill);
      try {
        const date = detail.date ?? status.date;
        const seconds = detail.seconds ?? status.seconds;
        if (!date) throw new Error('Historical date is not ready for tracking prefill.');
        if (wasPlayingBeforePrefill) HistoricalService.pause();

        const result = await prefillTrackingWindow(date, seconds);
        const windowFilled = result.injected >= result.requested && result.requested > 0;
        const readyForTrackingStart = result.ready || windowFilled;
        let setpointsApplied = false;
        let trackingStarted = false;
        let trackingMessage = '';

        if (readyForTrackingStart && detail.autoApplySetpoints) {
          try {
            const setpointResponse = await fetch('/api/twin/tracking/setpoints/historical-default', { method: 'POST' });
            setpointsApplied = setpointResponse.ok;
          } catch {
            setpointsApplied = false;
          }
        }

        if (readyForTrackingStart && detail.autoStartTracking) {
          try {
            const targetSnapshot = await fetchHistoricalSnapshotAt(date, seconds);
            await pushBmsToBuffer(
              historicalEpochFor(date, seconds),
              { date, seconds },
              targetSnapshot,
            );
            const currentResponse = await fetch('/api/twin/tracking/status');
            const currentStatus = currentResponse.ok ? await currentResponse.json().catch(() => null) : null;
            if (currentStatus?.running) {
              trackingStarted = true;
              trackingMessage = 'Tracking Twin already running';
              markTrackingRunAccepted(trackingRunKeyFromStatus(currentStatus));
            } else {
              const trackingOptions = detail.trackingOptions ?? {};
              const trackingRequestedAt = markTrackingRunRequested();
              const trackingResponse = await fetch('/api/twin/tracking/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tracking_delay_min: detail.source === 'auto_validation' ? 0 : 3,
                  ahu_sa_temp: 13,
                  zone_cooling_sp: 26,
                  chw_on: true,
                  chw_sp: 7,
                  cw_sp: 28,
                  bt4_sp: 7,
                  buffer_tank_sp: 7,
                  disable_throttle: trackingOptions.disable_throttle ?? false,
                  max_steps: trackingOptions.max_steps,
                  stop_after_epoch: trackingOptions.stop_after_epoch,
                  start_epoch: trackingOptions.start_epoch,
                  run_period_days: trackingOptions.run_period_days ?? 1,
                  require_ready: trackingOptions.require_ready ?? true,
                  allow_system_off_start: trackingOptions.allow_system_off_start ?? false,
                }),
              });
              const trackingPayload = await trackingResponse.json().catch(() => ({}));
              trackingStarted = trackingResponse.ok && trackingPayload?.ok === true;
              if (trackingStarted) {
                markTrackingRunAccepted(trackingPayload?.run_key ?? '');
              } else {
                markTrackingRunFailed(trackingRequestedAt);
              }
              trackingMessage = normalizeTrackingMessage(
                trackingPayload?.message,
                trackingStarted ? 'Tracking Twin을 시작했습니다.' : 'Tracking Twin 시작에 실패했습니다.',
                trackingStarted,
              );
            }
          } catch {
            trackingStarted = false;
            markTrackingRunFailed();
            trackingMessage = 'Tracking Twin 서버에 연결할 수 없습니다.';
          }
        }

        window.dispatchEvent(new CustomEvent('aees:tracking-window-prefilled', {
          detail: {
            ok: true,
            ...result,
            ready: readyForTrackingStart || trackingStarted,
            setpointsApplied,
            trackingStarted,
            trackingMessage,
          },
        }));
      } catch (error) {
        window.dispatchEvent(new CustomEvent('aees:tracking-window-prefilled', {
          detail: { ok: false, message: error instanceof Error ? error.message : 'Tracking prefill failed' },
        }));
      } finally {
        if (shouldResumeAfterPrefill && HistoricalService.getStatus().state === 'paused') {
          HistoricalService.resume();
        }
      }
    };
    window.addEventListener('aees:prefill-tracking-window', handlePrefill);
    return () => window.removeEventListener('aees:prefill-tracking-window', handlePrefill);
  }, [prefillTrackingWindow]);

  // ── HistoricalService → DataBuffer (이벤트 기반) ─────────────────────────
  // Historical 재생 매 틱마다 2018 epoch timestamp와 함께 DataBuffer에 주입
  // → TrackingTwin이 DataBuffer의 timestamp를 기준으로 EnergyPlus를 동기화
  useEffect(() => {
    const unsub = HistoricalService.subscribe(async (status) => {
      if (getPreferredDataSourceMode() !== 'historical') return;
      if (status.state === 'stopped') {
        if (DataBridge.getMode() === 'historical') DataBridge.setSource('simulation');
        return;
      }
      if (DataBridge.getMode() !== 'historical') DataBridge.setSource('historical');

      await pushBmsToBuffer(historicalEpochFor(status.date, status.seconds), {
        date: status.date,
        seconds: status.seconds,
      });
    });

    return unsub;
  }, [pushBmsToBuffer]);

  const autoValidationStartedRef = useRef(false);

  useEffect(() => {
    if (!authUser) return;

    const preferredSource = preferredDataSource;
    if (preferredSource !== 'historical') {
      HistoricalService.stop();
      DataBridge.setSource(preferredSource);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (cancelled || autoValidationStartedRef.current) return;
      autoValidationStartedRef.current = true;
      try {
        const restored = await HistoricalService.restore();
        if (cancelled) return;
        if (restored) {
          DataBridge.setSource('historical');
          if (HistoricalService.getStatus().speed > AUTO_VALIDATION_SPEED) {
            HistoricalService.setSpeed(AUTO_VALIDATION_SPEED);
          }
          const restoredStatus = HistoricalService.getStatus();
          const resumeAfterRecovery = restoredStatus.state === 'playing';
          if (resumeAfterRecovery) HistoricalService.pause();
          try {
            const prefill = await prefillTrackingWindow(
              restoredStatus.date,
              restoredStatus.seconds,
            );
            const windowFilled = prefill.injected >= prefill.requested && prefill.requested > 0;
            await pushBmsToBuffer(
              historicalEpochFor(restoredStatus.date, restoredStatus.seconds),
              { date: restoredStatus.date, seconds: restoredStatus.seconds },
            );

            const trackingStatusResponse = await fetch('/api/twin/tracking/status');
            const trackingStatus = trackingStatusResponse.ok
              ? await trackingStatusResponse.json().catch(() => null)
              : null;
            const retainedTrackingOutput = Boolean(
              (trackingStatus?.diagnostics?.output_count ?? 0) > 0
              || (
                trackingStatus?.diagnostics?.artifact_history?.available
                && (trackingStatus?.diagnostics?.artifact_history?.count ?? 0) > 0
              )
              || trackingStatus?.latest_output?.timestamp != null,
            );
            const trackingRestartRequired = Boolean(
              trackingStatus?.requires_process_restart
              || trackingStatus?.closed_loop_status?.requires_process_restart,
            );
            if (
              !trackingStatus?.running
              && !retainedTrackingOutput
              && !trackingRestartRequired
              && (prefill.ready || windowFilled)
            ) {
              await fetch('/api/twin/tracking/setpoints/historical-default', { method: 'POST' });
              const remainingSteps = Math.max(
                1,
                Math.floor(
                  (HISTORICAL_TEST_WINDOW_END_SECONDS - restoredStatus.seconds)
                  / EP_STEP_SECONDS,
                ),
              );
              const trackingRequestedAt = markTrackingRunRequested();
              const trackingResponse = await fetch('/api/twin/tracking/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tracking_delay_min: 0,
                  ahu_sa_temp: 13,
                  zone_cooling_sp: 26,
                  chw_on: true,
                  chw_sp: 7,
                  cw_sp: 28,
                  bt4_sp: 7,
                  buffer_tank_sp: 7,
                  disable_throttle: false,
                  max_steps: remainingSteps,
                  start_epoch: historicalEpochFor(restoredStatus.date, restoredStatus.seconds),
                  stop_after_epoch: historicalEpochFor(
                    restoredStatus.date,
                    HISTORICAL_TEST_WINDOW_END_SECONDS,
                  ),
                  run_period_days: 1,
                  require_ready: true,
                  allow_system_off_start: true,
                }),
              });
              const trackingPayload = await trackingResponse.json().catch(() => ({}));
              if (trackingResponse.ok && trackingPayload?.ok === true) {
                markTrackingRunAccepted(trackingPayload?.run_key ?? '');
              } else {
                markTrackingRunFailed(trackingRequestedAt);
              }
            }
          } finally {
            if (resumeAfterRecovery && HistoricalService.getStatus().state === 'paused') {
              HistoricalService.resume();
            }
          }
          window.dispatchEvent(new CustomEvent('aees:historical-restored', {
            detail: HistoricalService.getStatus(),
          }));
          return;
        }

        DataBridge.setSource('historical');
        await HistoricalService.start(
          AUTO_VALIDATION_DATE,
          AUTO_VALIDATION_SECONDS,
          AUTO_VALIDATION_SPEED,
          AUTO_VALIDATION_DATES,
        );
        if (cancelled) return;

        const startEpoch = historicalEpochFor(AUTO_VALIDATION_DATE, AUTO_VALIDATION_SECONDS);
        const stopAfterEpoch = historicalEpochFor(AUTO_VALIDATION_DATE, HISTORICAL_TEST_WINDOW_END_SECONDS);
        if (HistoricalService.isPlaying()) HistoricalService.pause();
        const result = await prefillTrackingWindow(AUTO_VALIDATION_DATE, AUTO_VALIDATION_SECONDS);
        const windowFilled = result.injected >= result.requested && result.requested > 0;
        let setpointsApplied = false;
        let trackingStarted = false;
        let trackingMessage = '';

        if (result.ready || windowFilled) {
          try {
            const setpointResponse = await fetch('/api/twin/tracking/setpoints/historical-default', { method: 'POST' });
            setpointsApplied = setpointResponse.ok;
          } catch {
            setpointsApplied = false;
          }

          try {
            const targetSnapshot = await fetchHistoricalSnapshotAt(AUTO_VALIDATION_DATE, AUTO_VALIDATION_SECONDS);
            await pushBmsToBuffer(
              historicalEpochFor(AUTO_VALIDATION_DATE, AUTO_VALIDATION_SECONDS),
              { date: AUTO_VALIDATION_DATE, seconds: AUTO_VALIDATION_SECONDS },
              targetSnapshot,
            );
            const trackingRequestedAt = markTrackingRunRequested();
            const trackingResponse = await fetch('/api/twin/tracking/start', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tracking_delay_min: 0,
                ahu_sa_temp: 13,
                zone_cooling_sp: 26,
                chw_on: true,
                chw_sp: 7,
                cw_sp: 28,
                bt4_sp: 7,
                buffer_tank_sp: 7,
                disable_throttle: false,
                max_steps: AUTO_VALIDATION_EP_STEPS,
                start_epoch: startEpoch,
                stop_after_epoch: stopAfterEpoch,
                run_period_days: 1,
                require_ready: true,
                allow_system_off_start: true,
              }),
            });
            const trackingPayload = await trackingResponse.json().catch(() => ({}));
            trackingStarted = trackingResponse.ok && trackingPayload?.ok === true;
            if (trackingStarted) {
              markTrackingRunAccepted(trackingPayload?.run_key ?? '');
            } else {
              markTrackingRunFailed(trackingRequestedAt);
            }
            trackingMessage = normalizeTrackingMessage(
              trackingPayload?.message,
              trackingStarted ? 'Tracking Twin을 시작했습니다.' : 'Tracking Twin 시작에 실패했습니다.',
              trackingStarted,
            );
          } catch {
            trackingStarted = false;
            markTrackingRunFailed();
            trackingMessage = 'Tracking Twin 서버에 연결할 수 없습니다.';
          }
        }

        window.dispatchEvent(new CustomEvent('aees:tracking-window-prefilled', {
          detail: {
            ok: true,
            ...result,
            ready: result.ready || windowFilled || trackingStarted,
            setpointsApplied,
            trackingStarted,
            trackingMessage,
          },
        }));
        if (HistoricalService.getStatus().state === 'paused') HistoricalService.resume();
      } catch (error) {
        if (HistoricalService.getStatus().state === 'paused') HistoricalService.resume();
        console.warn('[AEES] Auto validation bootstrap failed', error);
      }
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authUser, preferredDataSource, prefillTrackingWindow, pushBmsToBuffer]);

  // Live DataBuffer injection is enabled only for an actual measured live frame.
  useEffect(() => {
    const id = setInterval(() => {
      if (!HistoricalService.isActive() && TwinContextService.getFrame().source === 'live-measured') {
        pushBmsToBuffer();
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [pushBmsToBuffer]);

  // ── Tracking Twin → 외기온도만 BMS 캘리브레이션 (구역/전력은 Sim 건물 전용) ──
  useEffect(() => {
    const injectTwinData = async () => {
      try {
        const res = await fetch('/api/twin/tracking/status');
        if (!res.ok) return;
        const data = await res.json();
        const lo = data.latest_output;
        if (!lo || lo.timestamp === null) return;

        // 외기 온도만 BMS에 반영 (실측 보정) — 구역 온도/전력은 Sim 건물에서 직접 표시
        const overrides: Record<string, number> = {};
        if (lo.oat !== null) {
          overrides['DDC_G.D.D0100'] = lo.oat;
          overrides['AHU.D4003']     = lo.oat;
        }
        if (lo.ahu_sa_temp !== null) overrides['AHU.D4002'] = lo.ahu_sa_temp;
        if (lo.chw_supply !== null) overrides['DDC_M.D.D0103'] = lo.chw_supply;
        const zoneValues = Object.values(lo.zone_temps ?? {})
          .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
        if (zoneValues.length > 0) {
          const zonePointIds = ['DDC_M.D.D0116', 'DDC_M.D.D0117', 'DDC_M.D.D0118', 'DDC_M.D.D0119'];
          zonePointIds.forEach((pointId, index) => {
            overrides[pointId] = zoneValues[index % zoneValues.length];
          });
        }
        if (Object.keys(overrides).length > 0) {
          BMSSimulationService.injectOverrides(overrides);
        }
      } catch { /* tracking twin 오프라인 — 시뮬레이션 유지 */ }
    };

    injectTwinData();
    const twinInterval = setInterval(injectTwinData, 5000);
    return () => clearInterval(twinInterval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      for (let attempt = 1; attempt <= 3 && !cancelled; attempt += 1) {
        try {
          const response = await fetch('/api/models', { cache: 'no-store', headers: AuthService.authHeaders() });
          if (!response.ok) throw new Error(`Model API returned ${response.status}`);
          const data = await response.json() as Array<{ name: string; urn: string }>;
          if (!Array.isArray(data)) throw new Error('Model API returned an invalid payload');
          if (cancelled || data.length === 0) return;
          const activeConfig = BuildingConfigService.get();
          const facilityId = String(activeConfig.profile?.facilityId || activeConfig.meta.id || '').toLowerCase();
          const boundUrn = activeConfig.models?.revit?.status === 'ready' ? activeConfig.models.revit.urn || '' : '';
          if (boundUrn) {
            setPreferredRevitUrn(boundUrn);
            if (!modelUrn) setModelUrn(boundUrn);
            return;
          }
          if (facilityId !== 'sedc-01') {
            setPreferredRevitUrn('');
            return;
          }
          const savedUrn = readStorage(ACTIVE_MODEL_STORAGE_KEY);
          const saved = data.find(model => model.urn === savedUrn);
          const preferred = data.find(model => model.name === PREFERRED_REVIT_MODEL_NAME)
            ?? data.find(model => model.name.includes('0620SEDC'))
            ?? data.find(model => model.name.includes('0312SEDC'));
          if (preferred) setPreferredRevitUrn(preferred.urn);
          const selected = saved ?? preferred ?? data[0];
          if (!modelUrn && selected) setModelUrn(selected.urn);
          return;
        } catch (error) {
          if (attempt === 3) {
            console.warn('Revit model list is temporarily unavailable.', error);
            return;
          }
          await new Promise(resolve => window.setTimeout(resolve, attempt * 750));
        }
      }
    };
    void loadModels();
    return () => { cancelled = true; };
  }, [modelUrn, setModelUrn]);

  const renderOverlay = () => {
    if (activeTab === 'overview') return null;

    if (activeTab.startsWith('floor-')) {
      const floor = activeTab.split('-')[1].toUpperCase();
      return <FloorInfo floorId={floor} onBack={handleFloorInfoBack} />;
    }

    switch (activeTab) {
      case 'loop-water': return <SystemLoop loopType="water" />;
      case 'loop-air':   return <SystemLoop loopType="air" />;
      case 'ai-control': return <AIControl controlMode={controlMode} onModeChange={handleControlModeChange} onOpenExplainability={() => handleTabChange('ai-explain')} />;
      case 'ai-explain': return <AIExplainability />;
      case 'ai-model':   return <ModelManagement />;
      case 'digital-twin': return <DigitalTwin />;
      case 'reporting':  return <Reporting />;
      case 'settings':   return <Settings controlMode={controlMode} onModeChange={handleControlModeChange} modelUrn={modelUrn} onModelSelect={setModelUrn} />;
      default: return (
        <div className="flex flex-col items-center justify-center h-full space-y-4 text-slate-500">
          <i className="fa-solid fa-hammer text-5xl"></i>
          <h2 className="text-xl font-orbitron uppercase tracking-widest">{activeTab.replace('-', ' ')}</h2>
          <p className="text-sm">Calibration in progress...</p>
        </div>
      );
    }
  };

  const overlay = renderOverlay();
  const isOverview = activeTab === 'overview';
  const isEnergyPlusTestbed = preferredDataSource === 'energyplus';

  // ── 세션 확인 중 로딩 스피너 ──
  if (!authChecked) {
    return (
      <div className="h-screen w-full bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
          <p className="text-[10px] font-orbitron text-slate-500 uppercase tracking-widest">시스템 초기화 중...</p>
        </div>
      </div>
    );
  }

  // ── 미인증 → 로그인 화면 ──
  if (!authUser) {
    return <LoginPage onLoginSuccess={user => setAuthUser(user)} />;
  }

  return (
    <div className={`aees-light relative h-screen w-full overflow-hidden bg-[#e8eef1] text-[#142b38] ${isEnergyPlusTestbed ? 'aees-energyplus-testbed' : ''}`}>
      {/* Background Layer */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-[#e8eef1]"></div>

      {/* Home — 3D Digital Twin 메인 화면 */}
      {isOverview && (
        <div className="aees-tab-content absolute inset-0 z-10">
          {homeViewerMode === 'revit' && reviewModelUrn ? (
            <RevitModelReviewV2
              key={reviewModelUrn}
              modelUrn={reviewModelUrn}
              onViewerModeChange={setHomeViewerMode}
              onOpenSettings={() => setActiveTab('settings')}
            />
          ) : homeViewerMode === 'revit-prep' ? (
            <RevitViewerPreparing onCancel={() => setHomeViewerMode('operational')} />
          ) : home3DReady ? (
            <WorkspaceErrorBoundary key={`home-${homeViewerMode}`}>
              <Suspense fallback={<Home3DLoading />}>
                <Building3D
                  onTabChange={handleTabChange}
                  controlMode={controlMode}
                  onModeChange={handleControlModeChange}
                  dataSourceMode={preferredDataSource}
                  onDataSourceModeChange={handleDataSourceModeChange}
                  revitAvailable={Boolean(reviewModelUrn)}
                  onWorkspaceSelect={handleWorkspaceSelect}
                  initialCameraMode={overviewInitialCameraMode}
                />
              </Suspense>
            </WorkspaceErrorBoundary>
          ) : (
            <Home3DLoading />
          )}
        </div>
      )}

      {/* 다른 탭 오버레이 */}
      {overlay && (
        <main className="aees-tab-content absolute inset-0 z-20 flex h-full w-full flex-col bg-[#e8eef1] pb-16 md:pb-0 md:pl-24">
          <Header
            activeTab={activeTab}
            user={authUser}
            controlMode={controlMode}
            onModeChange={handleControlModeChange}
            dataSourceMode={preferredDataSource}
            onDataSourceModeChange={handleDataSourceModeChange}
            onLogout={handleLogout}
          />
          <div className="flex-1 overflow-auto custom-scrollbar relative">
            <WorkspaceErrorBoundary key={activeTab}>
              <Suspense fallback={<ViewLoading />}>
                {overlay}
              </Suspense>
            </WorkspaceErrorBoundary>
          </div>
        </main>
      )}

      <Chatbot
        sensorData={sensorData}
        activeTab={activeTab}
        dataSource={preferredDataSource === 'simulation' ? 'historical' : preferredDataSource}
        observedAt={TwinContextService.getFrame().timestamp
          ? new Date(TwinContextService.getFrame().timestamp as number).toISOString()
          : null}
        onNavigateTab={handleTabChange}
        onDataSourceModeChange={handleDataSourceModeChange}
      />
      {isEnergyPlusTestbed
        ? <EnergyPlusTestbedBar activeTab={activeTab} />
        : preferredDataSource === 'historical'
          ? <HistoricalPlayer activeTab={activeTab} />
          : null}

      {/* Floating Global Navigation */}
      <NavigationDock activeTab={activeTab} onTabChange={handleTabChange} onStartGuidedDemo={() => setGuidedDemoOpen(true)} />
      <GuidedDemo
        open={guidedDemoOpen}
        activeTab={activeTab}
        dataSourceMode={preferredDataSource}
        onOpenChange={setGuidedDemoOpen}
        onNavigateTab={handleTabChange}
        onDataSourceModeChange={handleDataSourceModeChange}
      />
      <UserActionFeedbackCenter />
    </div>
  );
};

export default App;
